import { useState } from 'react';
import { ApiError, api } from '@/lib/api';
import type { MarketingCampaignStatus } from '@/lib/types';
import { Button, ConfirmDialog } from '@/components/ui';

export interface CampaignStatusControlsProps {
  campaign: { id: number; name: string; status: MarketingCampaignStatus; startedAt: string | null };
  canSend: boolean;
  onChanged(): void;
}

type Action = 'pause' | 'resume' | 'cancel';

const PAUSABLE: readonly MarketingCampaignStatus[] = ['scheduled', 'sending'];
const CANCELLABLE: readonly MarketingCampaignStatus[] = ['pending_approval', 'scheduled', 'sending', 'paused'];

/**
 * Pause / Resume / Cancel. Each goes through a confirmation naming what
 * actually happens to queued and future sends. Renders nothing for a reader
 * without `marketing-campaigns.send`, or when no transition applies.
 */
export function CampaignStatusControls({ campaign, canSend, onChanged }: CampaignStatusControlsProps) {
  const [pending, setPending] = useState<Action | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canSend) return null;
  const canPause = PAUSABLE.includes(campaign.status);
  const canResume = campaign.status === 'paused';
  const canCancel = CANCELLABLE.includes(campaign.status);
  if (!canPause && !canResume && !canCancel) return null;

  async function run(action: Action) {
    setBusy(true);
    setError(null);
    try {
      if (action === 'pause') await api.pauseMarketingCampaign(campaign.id);
      else if (action === 'resume') await api.resumeMarketingCampaign(campaign.id);
      else await api.cancelMarketingCampaign(campaign.id);
      setPending(null);
      onChanged();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.formMessage ?? caught.message)
          : 'Could not reach the server. Nothing changed.',
      );
    } finally {
      setBusy(false);
    }
  }

  const copy: Record<Action, { title: string; description: string; confirm: string; tone: 'danger' | 'primary' }> = {
    pause: {
      title: `Pause ${campaign.name}?`,
      description:
        'No more emails go out until it is resumed. Emails already sent stay sent; queued ones wait.',
      confirm: 'Pause',
      tone: 'primary',
    },
    resume: {
      title: `Resume ${campaign.name}?`,
      description: campaign.startedAt
        ? 'Sending picks up where it stopped, within the account’s daily and per-minute limits.'
        : 'It goes back to scheduled and starts at its scheduled time, or straight away if that has passed.',
      confirm: 'Resume',
      tone: 'primary',
    },
    cancel: {
      title: `Cancel ${campaign.name}?`,
      description:
        'Every queued email is skipped and no recipient gets a later step. Emails already sent stay sent. This cannot be undone.',
      confirm: 'Cancel campaign',
      tone: 'danger',
    },
  };

  const current = pending ? copy[pending] : null;

  return (
    <>
      <div className="flex flex-wrap gap-2">
        {canPause && (
          <Button variant="secondary" size="sm" onClick={() => { setError(null); setPending('pause'); }}>
            Pause
          </Button>
        )}
        {canResume && (
          <Button variant="secondary" size="sm" onClick={() => { setError(null); setPending('resume'); }}>
            Resume
          </Button>
        )}
        {canCancel && (
          <Button variant="danger" size="sm" onClick={() => { setError(null); setPending('cancel'); }}>
            Cancel campaign
          </Button>
        )}
      </div>
      {current && pending && (
        <ConfirmDialog
          open
          onClose={() => {
            if (!busy) setPending(null);
          }}
          onConfirm={() => void run(pending)}
          title={current.title}
          description={current.description}
          confirmLabel={current.confirm}
          cancelLabel="Keep as is"
          tone={current.tone}
          loading={busy}
          error={error}
        />
      )}
    </>
  );
}
